import React from 'react';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSound } from '../hooks/useSound';
import { NeuralNetworkAnimation } from './NeuralNetworkAnimation';
import { Logo } from './Logo';

const whyPoints = [
  {
    title: "Data-Driven Decision Making",
    description: "AI-powered insights eliminate guesswork"
  },
  {
    title: "Expert Strategic Guidance",
    description: "Personalized recommendations ensure execution"
  },
  {
    title: "Real-Time Industry Benchmarking",
    description: "Track performance against competitors"
  },
  {
    title: "Continuous Optimization & Support",
    description: "We partner with you for sustainable success"
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const Hero = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 overflow-hidden bg-background">
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 5] }}>
          <ambientLight intensity={0.5} />
          <NeuralNetworkAnimation glowColor="#FFB800" />
        </Canvas>
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background" />

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        className="relative z-10 max-w-4xl mx-auto"
      >
        <motion.div
          className="relative flex justify-center mb-6"
          whileHover={{ scale: 1.05 }}
          transition={springConfig}
          onMouseEnter={playHover}
        >
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute inset-0 bg-amber-400/10 rounded-full filter blur-2xl"
          />
          <Logo className="text-amber-400" size="lg" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ...springConfig }}
        >
          <motion.div className="mb-4">
            <motion.h2 
              className="text-2xl md:text-3xl font-bold text-amber-400"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 1, ...springConfig }}
            >
              ProfitIQ.AI
            </motion.h2>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Transform Your Business with AI-Driven Intelligence
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-gray-300 mb-8"
          >
            Smarter Insights. Faster Growth. Sustainable Success.
          </motion.p>

          <motion.p
            className="text-lg text-gray-400 mb-12 max-w-3xl mx-auto"
          >
            At ProfitIQ.AI, we combine cutting-edge artificial intelligence with expert business guidance to help small and medium-sized businesses maximize efficiency, profitability, and scalability.
          </motion.p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 1.2, ...springConfig }}
        >
          {whyPoints.map((point, index) => (
            <motion.div
              key={point.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 + 0.4, duration: 0.8, ...springConfig }}
              className="flex items-start gap-3 text-left"
            >
              <CheckCircle2 className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-semibold">{point.title}</h3>
                <p className="text-gray-400">{point.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.4, ...springConfig }}
        >
          <motion.button
            className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
            onMouseEnter={playHover}
            onClick={() => {
              playClick();
              navigate('/consultation');
            }}
          >
            Get Started
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};